import { health, getUserMedicalTag } from '@/api/activity';

/** 个人信息必填字段（与 healthArchive 基本信息校验一致） */
export const PERSONAL_INFO_REQUIRED_FIELDS = [
  'name',
  'sex',
  'birthday',
  'height',
  'weight'
];

/** 需展示「降压药物」问题的个人疾病 */
const ANTIHYPERTENSIVE_DISEASES = ['高血压', '冠心病'];

/**
 * 字段是否已填写（null / undefined / 空字符串视为未填）
 * @param {*} value
 * @returns {boolean}
 */
export function isFieldFilled(value) {
  return value !== null && value !== undefined && value !== '';
}

/**
 * 是/否类布尔字段是否已选择（false 视为已填）
 * @param {*} value
 * @returns {boolean}
 */
export function isBooleanAnswerFilled(value) {
  return value !== null && value !== undefined;
}

/**
 * 个人信息必填项是否完整
 * @param {Object|null|undefined} userInfo
 * @returns {boolean}
 */
export function isPersonalInfoComplete(userInfo) {
  if (!userInfo) return false;
  return PERSONAL_INFO_REQUIRED_FIELDS.every((key) => isFieldFilled(userInfo[key]));
}

/**
 * 是否展示「降压药物」问题（与 healthArchive.showAntihypertensiveQuestion 一致）
 * @param {Array|null|undefined} personalTags - user-medical-tag type=1001
 * @returns {boolean}
 */
export function shouldShowAntihypertensiveQuestion(personalTags) {
  if (!Array.isArray(personalTags)) return false;
  return personalTags.some(
    (tag) => tag && ANTIHYPERTENSIVE_DISEASES.includes(String(tag.tagName || ''))
  );
}

/**
 * 是否展示抗精神病类/类固醇/勃起功能障碍药物问题（与 healthArchive.showAntipsychoticQuestion 一致）
 * @param {Array|null|undefined} mentalTags - user-medical-tag type=3001
 * @returns {boolean}
 */
export function shouldShowAntipsychoticMedicationQuestions(mentalTags) {
  if (!Array.isArray(mentalTags) || mentalTags.length === 0) return false;
  return mentalTags.some((tag) => {
    const name = tag && tag.tagName != null ? String(tag.tagName) : '';
    return name !== '无';
  });
}

/**
 * 药物相关问题是否已维护
 * @param {Object|null|undefined} userInfo
 * @param {Array|null|undefined} personalTags
 * @param {Array|null|undefined} mentalTags
 * @returns {boolean}
 */
export function isMedicationQuestionsComplete(userInfo, personalTags, mentalTags) {
  if (!userInfo) return false;

  if (shouldShowAntihypertensiveQuestion(personalTags)) {
    if (!isBooleanAnswerFilled(userInfo.hypertensionTreatment)) return false;
  }

  if (shouldShowAntipsychoticMedicationQuestions(mentalTags)) {
    if (!isBooleanAnswerFilled(userInfo.atypicalAntipsychotics)) return false;
    if (!isBooleanAnswerFilled(userInfo.oralCorticosteroids)) return false;
    const sex = userInfo.sex;
    if (sex == 1 || sex === '1') {
      if (!isBooleanAnswerFilled(userInfo.erectileDysfunction)) return false;
    }
  }

  return true;
}

/**
 * 疾病用药（healthArchive「疾病用药」tab）是否已维护
 * @param {Object|null|undefined} userInfo
 * @param {Array|null|undefined} mentalTags - user-medical-tag type=3001
 * @param {Array|null|undefined} personalTags - user-medical-tag type=1001
 * @returns {boolean}
 */
export function isDiseaseMedicationComplete(userInfo, mentalTags, personalTags) {
  if (!userInfo) return false;
  if (userInfo.medHis == null || userInfo.familyMedHis == null) return false;
  if (!isFieldFilled(userInfo.depressionEval)) return false;
  if (!Array.isArray(mentalTags) || mentalTags.length === 0) return false;
  if (!isMedicationQuestionsComplete(userInfo, personalTags, mentalTags)) return false;
  return true;
}

/**
 * 拉取个人史标签（medHis 为 true 时）
 * @param {Object} userInfo
 * @returns {Promise<Array|null>}
 */
async function fetchPersonalTagsIfNeeded(userInfo) {
  if (!userInfo || userInfo.medHis !== true) return null;
  try {
    const tagRes = await getUserMedicalTag({ type: '1001' });
    if (tagRes && (tagRes.code == 1000 || tagRes.code == '1000')) {
      return tagRes.data;
    }
  } catch (error) {
    console.error('获取个人疾病标签失败:', error);
  }
  return null;
}

/**
 * 拉取疾病用药校验所需的标签数据
 * @param {Object} userInfo
 * @returns {Promise<{ mentalTags: Array|null, personalTags: Array|null }>}
 */
async function loadDiseaseMedicationContext(userInfo) {
  let mentalTags = null;
  try {
    const tagRes = await getUserMedicalTag({ type: '3001' });
    if (tagRes && (tagRes.code == 1000 || tagRes.code == '1000')) {
      mentalTags = tagRes.data;
    }
  } catch (error) {
    console.error('获取精神类疾病标签失败:', error);
    mentalTags = null;
  }

  const personalTags = await fetchPersonalTagsIfNeeded(userInfo);
  return { mentalTags, personalTags };
}

/**
 * 评估疾病用药（含药物相关问题）是否已维护
 * @returns {Promise<{ complete: boolean, userData: Object|null }>}
 */
export async function evaluateDiseaseMedicationStatus() {
  try {
    const res = await health();
    if (!res || (res.code != 1000 && res.code != '1000') || !res.data) {
      return { complete: false, userData: null };
    }
    const userData = res.data;
    const { mentalTags, personalTags } = await loadDiseaseMedicationContext(userData);
    const complete = isDiseaseMedicationComplete(userData, mentalTags, personalTags);
    return { complete, userData };
  } catch (error) {
    console.error('评估疾病用药资料完整度失败:', error);
    return { complete: false, userData: null };
  }
}

/**
 * 评估健康报告页所需资料完整度
 * @returns {Promise<{ type: 'personal'|'disease'|null, userData: Object|null }>}
 */
export async function evaluateHealthProfileStatus() {
  try {
    const res = await health();
    if (!res || (res.code != 1000 && res.code != '1000') || !res.data) {
      return { type: 'personal', userData: null };
    }
    const userData = res.data;
    if (!isPersonalInfoComplete(userData)) {
      return { type: 'personal', userData };
    }

    const { mentalTags, personalTags } = await loadDiseaseMedicationContext(userData);

    if (!isDiseaseMedicationComplete(userData, mentalTags, personalTags)) {
      return { type: 'disease', userData };
    }
    return { type: null, userData };
  } catch (error) {
    console.error('评估健康资料完整度失败:', error);
    return { type: null, userData: null };
  }
}



// WEBPACK FOOTER //
// ./src/utils/healthReportProfileCheck.js